import React, { useEffect, useRef, useState } from "react";
import { IconButton, Stack, Typography, CircularProgress } from "@mui/material";

import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";

import axios from "axios";
import api from "../../api/endpoints";

interface VTBLikeButtonProps {
  articleId?: string | number;
}

const VTBLikeButton: React.FC<VTBLikeButtonProps> = ({ articleId }) => {
  const [likeCount, setLikeCount] = useState<number>(0);
  const [isLiked, setIsLiked] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const togglingRef = useRef(false);

  console.log("articleId", articleId);

  // ============================================================
  // USER DATA
  // ============================================================

  const customerData = sessionStorage.getItem("informaticscustomer_id");
  const usermasterId = sessionStorage.getItem("user_id");
  const consortiaId = sessionStorage.getItem("consortia_id");

  const customer_id = Number(customerData) || null;
  const usermaster_id = Number(usermasterId) || null;
  const consortia_id = Number(consortiaId) || null;

  // ============================================================
  // GET LIKE INFORMATION
  // ============================================================

  const fetchLikeInfo = async () => {
    if (!articleId) {
      return;
    }

    try {
      setLoading(true);

      const response = await axios.get(
        api.baseApi +
          `/api/get_article_like_by_id/get_article_like_by_id?article_id=${articleId}&profile_id=${usermasterId}`,
      );

      const data = response.data?.data?.[0];

      if (data) {
        setLikeCount(Number(data.liked_count ?? 0));

        if (typeof data.is_liked === "boolean") {
          setIsLiked(data?.is_liked);
        }
      } else {
        setLikeCount(0);
        setIsLiked(false);
      }
    } catch (error) {
      console.log("fetchLikeInfo error:", error);

      setLikeCount(0);
      setIsLiked(false);
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // LOAD LIKE INFO WHEN ARTICLE CHANGES
  // ============================================================

  useEffect(() => {
    fetchLikeInfo();
  }, [articleId]);

  // ============================================================
  // POST LIKE USAGE
  // ============================================================

  const postLikeUsage = async (liked: boolean) => {
    if (!articleId) {
      return;
    }

    const apiFormData = {
      data: [
        {
          customer_id: customer_id,
          usermaster_id: usermaster_id,
          consortia_id: consortia_id,
          article_id: articleId,
          is_liked: liked,
        },
      ],
    };

    const formData = new FormData();

    formData.append("detailed_json", JSON.stringify(apiFormData));

    try {
      await axios.post(
        api.baseApi + `/api/insert_video_likes_usage/insert_video_likes_usage`,
        formData,
      );
    } catch (error) {
      console.log("postLikeUsage error:", error);
      throw error;
    }
  };

  // ============================================================
  // TOGGLE LIKE
  // ============================================================

  const toggleLike = async () => {
    if (!articleId || togglingRef.current) {
      return;
    }

    togglingRef.current = true;

    const previousLiked = isLiked;
    const previousCount = likeCount;

    const nextLiked = !previousLiked;

    // ----------------------------------------------------------
    // OPTIMISTIC UI
    // ----------------------------------------------------------

    setIsLiked(nextLiked);

    setLikeCount(
      nextLiked ? previousCount + 1 : Math.max(0, previousCount - 1),
    );

    try {
      await postLikeUsage(nextLiked);
    } catch (error) {
      // --------------------------------------------------------
      // ROLLBACK
      // --------------------------------------------------------

      setIsLiked(previousLiked);
      setLikeCount(previousCount);

      console.log("toggleLike error:", error);
    } finally {
      togglingRef.current = false;
    }
  };

  // ============================================================
  // NO ARTICLE
  // ============================================================

  if (!articleId) {
    return null;
  }

  // ============================================================
  // UI
  // ============================================================

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <IconButton
        onClick={toggleLike}
        disabled={loading || togglingRef.current}
        color="primary"
      >
        {loading ? (
          <CircularProgress size={22} />
        ) : isLiked ? (
          <ThumbUpIcon />
        ) : (
          <ThumbUpOutlinedIcon />
        )}
      </IconButton>

      <Typography variant="body2">
        {likeCount.toLocaleString()} likes
      </Typography>
    </Stack>
  );
};

export default VTBLikeButton;
